import type {
  HealthActivityDeserializedData,
  PersistInputsMutationArgs,
  ToolboxTransformedComponent,
} from '@leagueplatform/health-journey-api';

/** Fields in the existing PCP delivery contract, independent of the form UI. */
export const PCP_ANSWER_FIELDS = [
  'pcp_name',
  'clinic_address_line_1',
  'clinic_address_line_2',
  'clinic_address_city',
  'clinic_address_state',
  'clinic_address_zip',
  'clinic_address_country',
  'clinic_phone_number',
  'user_mailing_address_line_1',
  'user_mailing_address_line_2',
  'user_mailing_address_city',
  'user_mailing_address_state',
  'user_mailing_address_zip',
] as const;

type AnswerField = (typeof PCP_ANSWER_FIELDS)[number];
type MailingField = Extract<AnswerField, `user_mailing_address_${string}`>;

export type PCPSubmissionInfo = Record<
  Exclude<AnswerField, MailingField>,
  string | null | undefined
> & {
  user_mailing_address: Record<
    'address_line_1' | 'address_line_2' | 'city' | 'state' | 'zip',
    string | null | undefined
  >;
};

const optionalFields = new Set<AnswerField>([
  'clinic_address_line_2',
  'user_mailing_address_line_2',
]);

/** Adapt the custom form to the SDK without rendering the activity's pages. */
export function buildPCPFhirSubmission(
  info: PCPSubmissionInfo,
  activity: HealthActivityDeserializedData,
): PersistInputsMutationArgs {
  const userHealthCampaignId = activity.userHealthCampaign?.data?.id;
  if (!activity.id || !userHealthCampaignId || activity.status !== 'active') {
    throw new Error(
      'PCP submission requires an active member activity and campaign',
    );
  }

  const answers: Record<AnswerField, string | null | undefined> = {
    pcp_name: info.pcp_name,
    clinic_address_line_1: info.clinic_address_line_1,
    clinic_address_line_2: info.clinic_address_line_2,
    clinic_address_city: info.clinic_address_city,
    clinic_address_state: info.clinic_address_state,
    clinic_address_zip: info.clinic_address_zip,
    clinic_address_country: info.clinic_address_country,
    clinic_phone_number: info.clinic_phone_number,
    user_mailing_address_line_1: info.user_mailing_address.address_line_1,
    user_mailing_address_line_2: info.user_mailing_address.address_line_2,
    user_mailing_address_city: info.user_mailing_address.city,
    user_mailing_address_state: info.user_mailing_address.state,
    user_mailing_address_zip: info.user_mailing_address.zip,
  };

  const configuredComponents =
    activity.pages?.flatMap((page) => page.components) ?? [];
  const seenLinkIds = new Set<string>();
  const seenComponentIds = new Set<string>();
  let questionnaireReference: string | undefined;
  const components = PCP_ANSWER_FIELDS.map((field) => {
    const matches = configuredComponents.filter((component) =>
      component.componentStorageSources?.some(
        (source) =>
          source.storageSourceType === 'fhir-questionnaire' &&
          source.storageSourceAttributes.code === field,
      ),
    );
    const component = matches[0];
    // One configured string input and one storage destination per PCP field.
    const source = component?.componentStorageSources?.[0];
    const attributes = source?.storageSourceAttributes;
    if (
      matches.length !== 1 ||
      component.componentType !== 'string-input' ||
      !component.id ||
      seenComponentIds.has(component.id) ||
      component.componentStorageSources?.length !== 1 ||
      source?.storageSourceType !== 'fhir-questionnaire' ||
      typeof attributes?.linkId !== 'string' ||
      !attributes.linkId.trim() ||
      seenLinkIds.has(attributes.linkId) ||
      typeof attributes.questionnaireReference !== 'string' ||
      !attributes.questionnaireReference.trim() ||
      (questionnaireReference &&
        questionnaireReference !== attributes.questionnaireReference)
    ) {
      throw new Error(`Invalid PCP question mapping: ${field}`);
    }
    seenComponentIds.add(component.id);
    seenLinkIds.add(attributes.linkId);
    questionnaireReference = attributes.questionnaireReference;
    const value = answers[field];
    const missing = value == null || value.trim() === '';
    if (missing && !optionalFields.has(field)) {
      throw new Error(`Missing PCP answer: ${field}`);
    }
    return {
      id: component.id,
      componentType: component.componentType,
      // SDK intersects all input answer types; string input answers need this
      // narrow cast until its transformed-attributes declaration is corrected.
      componentAttributes: {
        answer: missing ? null : value,
        required: !optionalFields.has(field),
      } as unknown as ToolboxTransformedComponent['componentAttributes'],
      componentStorageSources: component.componentStorageSources,
    };
  });

  return {
    userActivityId: activity.id,
    userHealthCampaignId,
    completedActivity: true,
    components,
  };
}
